"use client";

import { cn } from "@/lib/utils";
import { usePageCatalog } from "@app/_components/PageCatalog/PageCatalogReader";
import useTranslate from "@app/_hooks/useTranslate";
import { Button } from "@ui/button";
import { XIcon } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";
import { type ComponentProps } from "react";
import { isFilterParam } from "../const";
import removeUnwantedQueryParams from "../services/removeUnwantedQueryParams";

export default function ActiveFilter({
  className,
  identifier,
  ...props
}: { identifier: string } & ComponentProps<"ul">) {
  const t = useTranslate();
  const router = useRouter();
  const pageCatalog = usePageCatalog();
  const searchParams = useSearchParams();

  const filters: Array<{ key: string; value: string; label: string }> = [
    ...searchParams,
  ].flatMap(([key, value]) => {
    if (!isFilterParam(key)) return [];
    const id = value.replace(identifier, "");
    const translation = t(id, { strict: true });
    if (translation) {
      return { key, value, label: translation };
    }
    const pageCatalogEntry = pageCatalog?.get(id);
    if (pageCatalogEntry?.title) {
      return { key, value, label: pageCatalogEntry.title };
    }
    return { key, value, label: value };
  });

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const key = e.currentTarget.dataset.attribute;
    const value = e.currentTarget.dataset.value;
    const newSearchParams = new URLSearchParams(searchParams);
    if (key && value) {
      newSearchParams.delete(key, value);
    } else if (key) {
      newSearchParams.delete(key);
    }
    router.replace(`?${removeUnwantedQueryParams(newSearchParams).toString()}`);
  };

  if (!filters.length) return null;

  return (
    <ul className={cn("flex flex-row gap-2 p-2", className)} {...props}>
      {filters.map(({ key, value, label }) => (
        <Button
          key={`${key}:${value}`}
          variant="secondary"
          size="xs"
          data-attribute={key}
          data-value={value}
          asChild
          onClick={handleClick}
        >
          <li>
            {label}
            <XIcon className="inline-block size-3" />
          </li>
        </Button>
      ))}
    </ul>
  );
}
